import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import MaterialComparator from "@/components/MaterialComparator";
import GrupoVentasuelos from "@/components/GrupoVentasuelos";
import RoomSelector from "@/components/RoomSelector";
import { calculateAll } from "@/lib/calculations";
import { generatePDF } from "@/lib/pdfGenerator";
import { MATERIALS, UNITS, TOOL_LISTS, LEVELING_TOOLS, WELDING_TOOLS } from "@/lib/materialsData";
import { toast } from "sonner";
import {
  Ruler, ArrowLeft, ArrowRight, Upload, Calculator, FileText, Save,
  BarChart3, Check, Wrench, ChevronDown, ChevronUp, Download, Brain,
  Loader2, Square, X, History, Clock,
} from "lucide-react";
import { authFetch } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ALLOWED_MATERIALS = [
  "Suelo laminado", "Parquet", "Suelo vinílico", "LVT",
  "Moqueta", "Linóleo", "Tarima de exterior", "Césped artificial",
];

const filterRecommendations = (materials) => {
  if (!materials || !materials.length) return [];
  const filtered = materials.filter((m) =>
    ALLOWED_MATERIALS.some((a) => m.toLowerCase().includes(a.toLowerCase()) || a.toLowerCase().includes(m.toLowerCase()))
  );
  return filtered.length > 0 ? filtered : [];
};
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { num: 1, label: "Superficie", icon: Square },
  { num: 2, label: "Material", icon: Ruler },
  { num: 3, label: "Instalación", icon: Wrench },
  { num: 4, label: "Resultados", icon: Calculator },
];

const initialForm = {
  project_name: "",
  surface_m2: "",
  width: "",
  length: "",
  unit: "m",
  material_type: "",
  format_type: "",
  piece_width: "",
  piece_length: "",
  piece_unit: "cm",
  quantity_per_box: "",
  installation_type: "",
  installation_pattern: "Recta",
  soldadura_termica: "No",
  rodapie_enabled: false,
  rodapie_length: "",
  rodapie_strip_length: "2.4",
  pavimento_esd: false,
  nivelacion: false,
  herramientas: false,
  room_type: "",
  blueprint_file: null,
};

export default function ProjectWizard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [comparatorOpen, setComparatorOpen] = useState(false);
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [blueprintPreview, setBlueprintPreview] = useState(null);
  const [aiRooms, setAiRooms] = useState([]);
  const [aiScale, setAiScale] = useState(null);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [selectedRoomIndices, setSelectedRoomIndices] = useState([]);
  const isEditing = !!projectId && projectId !== "new";

  // Load existing project
  useEffect(() => {
    if (isEditing) {
      (async () => {
        try {
          const res = await authFetch(`${API}/projects/${projectId}`);
          if (res.ok) {
            const data = await res.json();
            setForm((prev) => ({
              ...prev,
              ...data,
              surface_m2: data.surface_m2 || "",
              rodapie_enabled: data.rodapie_enabled || false,
              rodapie_strip_length: data.rodapie_strip_length || "2.4",
              pavimento_esd: data.pavimento_esd || false,
              nivelacion: data.nivelacion || false,
              herramientas: data.herramientas || false,
            }));
            if (data.calculation_results) {
              setResults(data.calculation_results);
              setStep(4);
            }
            if (data.blueprint_preview) setBlueprintPreview(data.blueprint_preview);
          }
          // Fetch version history
          const vRes = await authFetch(`${API}/projects/${projectId}/versions`);
          if (vRes.ok) setVersions(await vRes.json());
        } catch {
          toast.error("Error cargando proyecto");
        }
      })();
    }
  }, [isEditing, projectId]);

  // Estimate perimeter from rooms or manual dimensions
  const estimatePerimeter = useCallback((rooms, width, length, unit) => {
    // From selected rooms: prefer AI perimeter_without_doors_m, fallback to 4*sqrt(m2)
    if (rooms && rooms.length > 0) {
      let totalPerimeter = 0;
      let hasAiPerimeter = false;
      rooms.forEach((r) => {
        const aiPerim = parseFloat(r.perimeter_without_doors_m);
        if (aiPerim > 0) {
          totalPerimeter += aiPerim;
          hasAiPerimeter = true;
        } else {
          const m2 = parseFloat(r.estimated_m2) || 0;
          if (m2 > 0) totalPerimeter += 4 * Math.sqrt(m2);
        }
      });
      if (totalPerimeter > 0) return { value: Math.round(totalPerimeter * 100) / 100, fromAi: hasAiPerimeter };
    }
    // From manual width x length
    if (width && length) {
      const k = unit === "m" ? 1 : unit === "cm" ? 0.01 : 0.001;
      const w = parseFloat(width) * k;
      const l = parseFloat(length) * k;
      if (w > 0 && l > 0) return { value: Math.round(2 * (w + l) * 100) / 100, fromAi: false };
    }
    return { value: 0, fromAi: false };
  }, []);

  const updateForm = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Reset dependent fields
      if (key === "material_type") {
        next.format_type = "";
        next.installation_type = "";
        next.soldadura_termica = "No";
        next.pavimento_esd = false;
      }
      if (key === "format_type") {
        next.installation_type = "";
        next.base_aislante_type = "";
        next.base_aislante_m2_roll = "";
      }
      if (key === "installation_type") {
        next.base_aislante_type = "";
        next.base_aislante_m2_roll = "";
      }
      // Auto-fill rodapie perimeter when toggled on
      if (key === "rodapie_enabled" && value === true && !prev.rodapie_length) {
        const selectedRooms = selectedRoomIndices.map((i) => aiRooms[i]).filter(Boolean);
        const perim = estimatePerimeter(selectedRooms, prev.width, prev.length, prev.unit);
        if (perim.value > 0) next.rodapie_length = String(perim.value);
      }
      return next;
    });
  };

  const material = form.material_type ? MATERIALS[form.material_type] : null;
  const formats = material ? material.formats : [];
  const installTypes = material && form.format_type ? (material.installationTypes[form.format_type] || []) : [];

  // Calculate surface from width x length
  useEffect(() => {
    if (form.width && form.length && form.unit) {
      const k = form.unit === "m" ? 1 : form.unit === "cm" ? 0.01 : 0.001;
      const m2 = (parseFloat(form.width) * k) * (parseFloat(form.length) * k);
      if (m2 > 0) updateForm("surface_m2", m2.toFixed(2));
    }
  }, [form.width, form.length, form.unit]); // eslint-disable-line react-hooks/exhaustive-deps

  // AI Plan Analysis
  const handlePlanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    updateForm("blueprint_file", file);
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authFetch(`${API}/analyze-plan`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        if (data.room_type && data.room_type !== "Desconocido") {
          updateForm("room_type", data.room_type);
        }
        if (data.estimated_m2) {
          updateForm("surface_m2", String(data.estimated_m2));
        }
        if (data.width) updateForm("width", String(data.width));
        if (data.length) updateForm("length", String(data.length));
        if (data.blueprint_preview) setBlueprintPreview(data.blueprint_preview);
        if (data.rooms_detected) setAiRooms(data.rooms_detected);
        if (data.scale) setAiScale(data.scale);
        if (data.recommended_materials) setAiRecommendations(filterRecommendations(data.recommended_materials));
        toast.success("Plano analizado con IA");
        if (data.multipage_warning) toast.warning("El PDF tiene " + data.multipage_warning + " páginas: solo se ha analizado la página 1", { duration: 9000 });
      } else if (res.status === 401) {
        toast.error("Sesión expirada. Por favor, inicia sesión de nuevo.");
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(res.headers.get("x-error-detail") || errData.detail || "Error en el análisis del plano");
      }
    } catch {
      toast.error("Error al subir el plano");
    } finally {
      setAnalyzing(false);
    }
  };

  // Room selection from AI-detected rooms (multi-select)
  const handleRoomSelect = (indices, rooms) => {
    setSelectedRoomIndices(indices);
    if (rooms && rooms.length > 0) {
      // Sum m2 from all selected rooms
      const totalM2 = rooms.reduce((sum, r) => sum + (r.estimated_m2 ? parseFloat(r.estimated_m2) : 0), 0);
      if (totalM2 > 0) updateForm("surface_m2", String(totalM2.toFixed(2)));
      // Use the first room's type as room_type, or combine names
      const firstRoomWithType = rooms.find(r => r.type && r.type !== "Desconocido");
      if (firstRoomWithType) {
        updateForm("room_type", firstRoomWithType.type);
      } else if (rooms.length === 1 && rooms[0].name) {
        updateForm("room_type", rooms[0].name);
      }
      // Auto-fill rodapie perimeter if enabled
      if (form.rodapie_enabled) {
        const perim = estimatePerimeter(rooms, null, null, null);
        if (perim.value > 0) updateForm("rodapie_length", String(perim.value));
      }
    } else {
      // All deselected - reset
      updateForm("surface_m2", "");
      updateForm("room_type", "");
    }
  };

  // Room editing (delete, resize, add) from RoomSelector
  const handleRoomsChange = (newRooms) => {
    setAiRooms(newRooms);
    // Clean up selected indices that exceed the new rooms length
    setSelectedRoomIndices(prev => prev.filter(i => i < newRooms.length));
  };

  // Run calculation
  const handleCalculate = () => {
    if (!form.surface_m2 || parseFloat(form.surface_m2) <= 0) {
      toast.error("Introduce una superficie valida");
      return;
    }
    const calcResults = calculateAll(form);
    setResults(calcResults);
    setStep(4);
  };

  // Save project
  const handleSave = async () => {
    if (!form.project_name.trim()) {
      toast.error("Introduce un nombre para el proyecto");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        project_name: form.project_name,
        surface_m2: parseFloat(form.surface_m2) || null,
        material_type: form.material_type || null,
        format_type: form.format_type || null,
        installation_type: form.installation_type || null,
        installation_pattern: form.installation_pattern,
        room_type: form.room_type || null,
        calculation_results: results,
        estimated_budget: results?.budget || null,
        blueprint_preview: blueprintPreview || null,
        width: parseFloat(form.width) || null,
        length: parseFloat(form.length) || null,
        unit: form.unit,
        piece_width: parseFloat(form.piece_width) || null,
        piece_length: parseFloat(form.piece_length) || null,
        piece_unit: form.piece_unit,
        quantity_per_box: parseFloat(form.quantity_per_box) || null,
        soldadura_termica: form.soldadura_termica,
        rodapie_enabled: form.rodapie_enabled,
        rodapie_length: parseFloat(form.rodapie_length) || null,
        rodapie_strip_length: form.rodapie_strip_length,
        pavimento_esd: form.pavimento_esd,
        nivelacion: form.nivelacion,
        herramientas: form.herramientas,
      };

      const url = isEditing ? `${API}/projects/${projectId}` : `${API}/projects`;
      const method = isEditing ? "PUT" : "POST";

      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Proyecto guardado correctamente");
        navigate("/dashboard");
      } else {
        toast.error("Error guardando proyecto");
      }
    } catch {
      toast.error("Error de conexion");
    } finally {
      setSaving(false);
    }
  };

  // PDF Download
  const handlePDF = () => {
    try {
      // Build tool list for PDF
      let toolData = null;
      if (form.herramientas) {
        const toolKey = getToolListKey();
        if (toolKey && TOOL_LISTS[toolKey]) {
          toolData = { main: TOOL_LISTS[toolKey] };
          if (form.soldadura_termica !== "No") {
            toolData.welding = WELDING_TOOLS;
          }
          if (form.nivelacion) {
            toolData.leveling = LEVELING_TOOLS;
          }
        }
      }
      generatePDF(form.project_name, form, results, form.room_type, toolData);
      toast.success("PDF descargado");
    } catch (err) {
      console.error("Error generando PDF:", err);
      toast.error("Error al generar el PDF");
    }
  };

  // Get tool list key - cascades through format alternatives
  const getToolListKey = () => {
    const mt = form.material_type;
    const ft = form.format_type;
    const it = form.installation_type;
    if (!mt || !it) return null;

    // 1. Exact match: material_format_installation
    const exactKey = `${mt}_${ft}_${it}`;
    if (TOOL_LISTS[exactKey]) return exactKey;

    // 2. Without format: material_installation
    const noFmtKey = `${mt}_${it}`;
    if (TOOL_LISTS[noFmtKey]) return noFmtKey;

    // 3. Try alternate formats (Loseta↔Lama↔Rollo)
    const altFormats = ["Lama", "Loseta", "Rollo"];
    for (const af of altFormats) {
      if (af === ft) continue;
      const altKey = `${mt}_${af}_${it}`;
      if (TOOL_LISTS[altKey]) return altKey;
    }

    // 4. Last resort: any key for this material
    for (const k of Object.keys(TOOL_LISTS)) {
      if (k.startsWith(`${mt}_`)) return k;
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#4A5568] hover:text-[#1B2B4D]" data-testid="back-to-dashboard">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">Volver</span>
          </button>
          <div className="flex items-center gap-2">
            <img src="/planimetrik-header.png" alt="Planimetrik" className="h-6 object-contain" />
          </div>
        </div>
      </header>

      {/* Step Indicator */}
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => (
              <div key={s.num} className="flex items-center">
                <button
                  onClick={() => { if (s.num < step || (s.num <= 3 && step >= s.num)) setStep(s.num); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    step === s.num ? "bg-[#0066FF] text-white" :
                    step > s.num ? "bg-[#1B2B4D] text-white" : "bg-[#E2E8F0] text-[#4A5568]"
                  }`}
                  data-testid={`step-btn-${s.num}`}
                >
                  {step > s.num ? <Check className="h-3 w-3" /> : <s.icon className="h-3 w-3" />}
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`w-8 sm:w-16 h-0.5 mx-1 ${step > s.num ? "bg-[#1B2B4D]" : "bg-[#E2E8F0]"}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Project Name */}
        <div className="mb-4">
          <Input
            data-testid="project-name-input"
            placeholder="Nombre del proyecto..."
            value={form.project_name}
            onChange={(e) => updateForm("project_name", e.target.value)}
            className="font-heading text-lg font-semibold text-[#1B2B4D] border-none bg-transparent px-0 h-auto focus-visible:ring-0 placeholder:text-[#CBD5E0]"
          />
        </div>

        {/* ─── STEP 1: Surface ─── */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-in-up">
            <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D] flex items-center gap-2">
                  <Upload className="h-4 w-4 text-[#0066FF]" />
                  Subir Plano (opcional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="upload-zone rounded-lg p-8 text-center cursor-pointer relative" onClick={() => document.getElementById("plan-upload").click()}>
                  <input
                    id="plan-upload"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handlePlanUpload}
                    className="hidden"
                    data-testid="plan-upload-input"
                  />
                  {analyzing ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-8 w-8 text-[#0066FF] animate-spin mb-2" />
                      <p className="text-sm text-[#4A5568]">Analizando plano con IA...</p>
                    </div>
                  ) : form.blueprint_file ? (
                    <div className="flex flex-col items-center">
                      <Check className="h-8 w-8 text-green-500 mb-2" />
                      <p className="text-sm font-medium text-[#1B2B4D]">{form.blueprint_file.name}</p>
                      <p className="text-xs text-[#4A5568] mt-1">Plano cargado</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-8 w-8 text-[#CBD5E0] mb-2" />
                      <p className="text-sm font-medium text-[#4A5568]">Arrastra o selecciona un plano</p>
                      <p className="text-xs text-[#CBD5E0] mt-1">PDF o imagen (JPG, PNG)</p>
                    </div>
                  )}
                </div>
                {form.room_type && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Brain className="h-4 w-4 text-[#FFA500]" />
                    <Badge className="bg-[#FFF3E0] text-[#E65100] border-[#FFA500]/20">
                      Estancia: {form.room_type}
                    </Badge>
                    {aiScale && (
                      <Badge className="bg-[#E3F2FD] text-[#0066FF] border-[#0066FF]/20">
                        Escala: {aiScale}
                      </Badge>
                    )}
                    {aiRecommendations.length > 0 && (
                      <div className="w-full mt-2">
                        <p className="text-xs text-[#4A5568] mb-1">Materiales recomendados:</p>
                        <div className="flex flex-wrap gap-1">
                          {aiRecommendations.map((m, i) => (
                            <Badge key={i} variant="secondary" className="bg-[#F0F4F8] text-[#4A5568] border-[#E2E8F0] text-[10px]">{m}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {aiRooms.length > 1 && (
                      <div className="w-full mt-2">
                        <p className="text-xs text-[#4A5568] mb-1">Estancias detectadas:</p>
                        <div className="flex flex-wrap gap-1">
                          {aiRooms.map((r, i) => (
                            <Badge key={i} className="bg-[#FFF3E0] text-[#E65100] border-[#FFA500]/20 text-[10px]">
                              {r.name || r.type} {r.estimated_m2 ? `(${r.estimated_m2} m2)` : ""}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {blueprintPreview && !aiRooms.length && (
                  <div className="mt-3">
                    <img src={blueprintPreview} alt="Plano" className="max-h-[120px] rounded-lg border border-[#E2E8F0] object-contain" data-testid="blueprint-preview" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Room Selector - appears when blueprint is uploaded */}
            {blueprintPreview && (
              <RoomSelector
                blueprintPreview={blueprintPreview}
                aiRooms={aiRooms}
                onRoomSelect={handleRoomSelect}
                selectedRooms={selectedRoomIndices}
                onRoomsChange={handleRoomsChange}
              />
            )}

            <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D]">
                  Superficie Manual
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">Ancho</Label>
                    <Input
                      data-testid="width-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={form.width}
                      onChange={(e) => updateForm("width", e.target.value)}
                      className="h-11 border-[#CBD5E0]"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">Largo</Label>
                    <Input
                      data-testid="length-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={form.length}
                      onChange={(e) => updateForm("length", e.target.value)}
                      className="h-11 border-[#CBD5E0]"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">Unidad</Label>
                    <Select value={form.unit} onValueChange={(v) => updateForm("unit", v)}>
                      <SelectTrigger className="h-11 border-[#CBD5E0]" data-testid="unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Separator />
                <div>
                  <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">O introduce m² directamente</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="surface-m2-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={form.surface_m2}
                      onChange={(e) => updateForm("surface_m2", e.target.value)}
                      className="h-11 border-[#CBD5E0] max-w-[200px]"
                    />
                    <span className="text-sm font-medium text-[#4A5568]">m²</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                data-testid="next-step-1"
                onClick={() => setStep(2)}
                disabled={!form.surface_m2 || parseFloat(form.surface_m2) <= 0}
                className="bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium"
              >
                Siguiente <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ─── STEP 2: Material ─── */}
        {step === 2 && (
          <div className="space-y-4 animate-fade-in-up">
            <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D]">
                    Selección de Material
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setComparatorOpen(true)}
                    className="text-[#0066FF] border-[#0066FF]/30 hover:bg-[#E3F2FD]"
                    data-testid="open-comparator-btn"
                  >
                    <BarChart3 className="h-4 w-4 mr-1" /> Comparar materiales
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Material */}
                <div>
                  <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">Tipo de Material</Label>
                  <Select value={form.material_type} onValueChange={(v) => updateForm("material_type", v)}>
                    <SelectTrigger className="h-11 border-[#CBD5E0]" data-testid="material-select">
                      <SelectValue placeholder="Selecciona material..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(MATERIALS).map(([id, m]) => (
                        <SelectItem key={id} value={id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Format */}
                {formats.length > 0 && (
                  <div>
                    <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">Formato</Label>
                    <Select value={form.format_type} onValueChange={(v) => updateForm("format_type", v)}>
                      <SelectTrigger className="h-11 border-[#CBD5E0]" data-testid="format-select">
                        <SelectValue placeholder="Selecciona formato..." />
                      </SelectTrigger>
                      <SelectContent>
                        {formats.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Piece dimensions */}
                {form.format_type && (
                  <div>
                    <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">
                      Dimensiones de la {form.format_type === "Rollo" ? "pieza (rollo)" : "pieza"}
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Input
                          data-testid="piece-width-input"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Ancho"
                          value={form.piece_width}
                          onChange={(e) => updateForm("piece_width", e.target.value)}
                          className="h-11 border-[#CBD5E0]"
                        />
                      </div>
                      <div>
                        <Input
                          data-testid="piece-length-input"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Largo"
                          value={form.piece_length}
                          onChange={(e) => updateForm("piece_length", e.target.value)}
                          className="h-11 border-[#CBD5E0]"
                        />
                      </div>
                      <Select value={form.piece_unit} onValueChange={(v) => updateForm("piece_unit", v)}>
                        <SelectTrigger className="h-11 border-[#CBD5E0]" data-testid="piece-unit-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Quantity per box */}
                {(form.format_type === "Lama" || form.format_type === "Loseta") && (
                  <div>
                    <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">Cantidad por caja (m²)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid="quantity-per-box-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={form.quantity_per_box}
                        onChange={(e) => updateForm("quantity_per_box", e.target.value)}
                        className="h-11 border-[#CBD5E0] max-w-[200px]"
                      />
                      <span className="text-sm text-[#4A5568]">m²</span>
                    </div>
                  </div>
                )}

                {/* Installation pattern (Lama only) */}
                {form.format_type === "Lama" && (
                  <div>
                    <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">Patrón de colocación</Label>
                    <Select value={form.installation_pattern} onValueChange={(v) => updateForm("installation_pattern", v)}>
                      <SelectTrigger className="h-11 border-[#CBD5E0]" data-testid="pattern-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Recta">Recta (+7%)</SelectItem>
                        <SelectItem value="Espiga">Espiga (+20%)</SelectItem>
                        <SelectItem value="Diagonal">Diagonal (+15%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} className="border-[#E2E8F0]" data-testid="prev-step-2">
                <ArrowLeft className="h-4 w-4 mr-1" /> Anterior
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!form.material_type || !form.format_type}
                className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
                data-testid="next-step-2"
              >
                Siguiente <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            <MaterialComparator
              roomType={form.room_type}
              rooms={selectedRoomIndices.map((i) => aiRooms[i]).filter(Boolean)}
              onSelect={(id) => updateForm("material_type", id)}
              open={comparatorOpen}
              onOpenChange={setComparatorOpen}
            />
          </div>
        )}

        {/* ─── STEP 3: Installation ─── */}
        {step === 3 && (
          <div className="space-y-4 animate-fade-in-up">
            <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D]">
                  Tipo de Instalación
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">Tipo de instalaci&#243;n</Label>
                  <Select value={form.installation_type} onValueChange={(v) => updateForm("installation_type", v)}>
                    <SelectTrigger className="h-11 border-[#CBD5E0]" data-testid="installation-type-select">
                      <SelectValue placeholder="Selecciona tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {installTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Base aislante (for any Flotante variant) */}
                {form.installation_type && form.installation_type.toLowerCase().includes("flotante") && (
                  <div>
                    <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">Base aislante</Label>
                    <Select value={form.base_aislante_type || ""} onValueChange={(v) => updateForm("base_aislante_type", v)}>
                      <SelectTrigger className="h-11 border-[#CBD5E0]" data-testid="base-aislante-select">
                        <SelectValue placeholder="Selecciona tipo..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="incorporada">Base incorporada</SelectItem>
                        <SelectItem value="aparte">Base aparte</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.base_aislante_type === "aparte" && (
                      <div className="mt-2">
                        <Label className="text-xs text-[#4A5568] mb-1 block">m² por rollo</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            data-testid="base-aislante-m2-input"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Ej: 10"
                            value={form.base_aislante_m2_roll || ""}
                            onChange={(e) => updateForm("base_aislante_m2_roll", e.target.value)}
                            className="h-11 border-[#CBD5E0] max-w-[200px]"
                          />
                          <span className="text-sm text-[#4A5568]">m²/rollo</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Soldadura térmica */}
                {material?.soldaduraApplicable && (
                  <div>
                    <Label className="text-sm font-semibold text-[#1B2B4D] mb-1.5 block">Soldadura térmica</Label>
                    <Select value={form.soldadura_termica} onValueChange={(v) => updateForm("soldadura_termica", v)}>
                      <SelectTrigger className="h-11 border-[#CBD5E0]" data-testid="soldadura-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Cordon 50 ml">Cordon 50 ml</SelectItem>
                        <SelectItem value="Cordon 100 ml">Cordon 100 ml</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* ESD */}
                {material?.esdApplicable && (
                  <div className="flex items-center justify-between py-2">
                    <Label className="text-sm font-semibold text-[#1B2B4D]">Pavimento ESD</Label>
                    <Switch
                      checked={form.pavimento_esd}
                      onCheckedChange={(v) => updateForm("pavimento_esd", v)}
                      data-testid="esd-switch"
                    />
                  </div>
                )}

                <Separator />

                {/* Rodapie */}
                <div>
                  <div className="flex items-center justify-between py-2">
                    <Label className="text-sm font-semibold text-[#1B2B4D]">Rodapié</Label>
                    <Switch
                      checked={form.rodapie_enabled}
                      onCheckedChange={(v) => updateForm("rodapie_enabled", v)}
                      data-testid="rodapie-switch"
                    />
                  </div>
                  {form.rodapie_enabled && (
                    <div className="mt-2 space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-[#4A5568] mb-1 block">Perímetro (m)</Label>
                          <Input
                            data-testid="rodapie-length-input"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Metros lineales"
                            value={form.rodapie_length}
                            onChange={(e) => updateForm("rodapie_length", e.target.value)}
                            className="h-11 border-[#CBD5E0]"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-[#4A5568] mb-1 block">Medida tira</Label>
                          <Select value={form.rodapie_strip_length} onValueChange={(v) => updateForm("rodapie_strip_length", v)}>
                            <SelectTrigger className="h-11 border-[#CBD5E0]" data-testid="rodapie-strip-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="2">2.00 m</SelectItem>
                              <SelectItem value="2.2">2.20 m</SelectItem>
                              <SelectItem value="2.25">2.25 m</SelectItem>
                              <SelectItem value="2.4">2.40 m</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {form.rodapie_length && (selectedRoomIndices.length > 0 || (form.width && form.length)) && (() => {
                        const selRooms = selectedRoomIndices.map((i) => aiRooms[i]).filter(Boolean);
                        const hasAiPerim = selRooms.some((r) => parseFloat(r.perimeter_without_doors_m) > 0);
                        return (
                          <p className="text-xs text-[#0066FF]" data-testid="rodapie-auto-hint">
                            {hasAiPerim
                              ? "Perímetro calculado por IA (puertas y aberturas descontadas). Puedes ajustarlo."
                              : "Perímetro estimado automáticamente. Puedes ajustarlo."}
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Nivelación */}
                <div className="flex items-center justify-between py-2">
                  <Label className="text-sm font-semibold text-[#1B2B4D]">Nivelación del soporte</Label>
                  <Switch
                    checked={form.nivelacion}
                    onCheckedChange={(v) => updateForm("nivelacion", v)}
                    data-testid="nivelacion-switch"
                  />
                </div>

                {/* Herramientas */}
                <div className="flex items-center justify-between py-2">
                  <Label className="text-sm font-semibold text-[#1B2B4D]">Herramientas</Label>
                  <Switch
                    checked={form.herramientas}
                    onCheckedChange={(v) => updateForm("herramientas", v)}
                    data-testid="herramientas-switch"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} className="border-[#E2E8F0]" data-testid="prev-step-3">
                <ArrowLeft className="h-4 w-4 mr-1" /> Anterior
              </Button>
              <Button
                onClick={handleCalculate}
                className="bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium"
                data-testid="calculate-btn"
              >
                <Calculator className="h-4 w-4 mr-1" /> Calcular
              </Button>
            </div>
          </div>
        )}

        {/* ─── STEP 4: Results ─── */}
        {step === 4 && results && (
          <div className="space-y-4 animate-fade-in-up">
            {/* Material Results */}
            <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D]">
                  Cálculo de Material
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-[#F0F4F8] rounded-lg p-3">
                    <p className="text-xs text-[#4A5568]">m² netos</p>
                    <p className="text-lg font-heading font-bold text-[#1B2B4D]" data-testid="m2-netos">{results.m2_netos}</p>
                  </div>
                  <div className="bg-[#FFF3E0] rounded-lg p-3">
                    <p className="text-xs text-[#4A5568]">Desperdicio (+{results.waste_percent}%)</p>
                    <p className="text-lg font-heading font-bold text-[#E65100]" data-testid="m2-desperdicio">{results.m2_desperdicio}</p>
                  </div>
                  <div className="bg-[#E3F2FD] rounded-lg p-3">
                    <p className="text-xs text-[#4A5568]">m² totales</p>
                    <p className="text-lg font-heading font-bold text-[#0066FF]" data-testid="m2-totales">{results.m2_totales}</p>
                  </div>
                </div>

                {results.boxes && (
                  <div className="mt-4 p-3 bg-[#F7FAFC] rounded-lg">
                    <p className="text-sm text-[#4A5568]">
                      Cajas necesarias: <span className="font-semibold text-[#1B2B4D]" data-testid="boxes-total">{results.boxes.total}</span>
                      {" "}&#8212; {results.boxes.m2_per_box} m²/caja = {results.boxes.total_m2_supplied} m²
                    </p>
                  </div>
                )}
                {results.rolls && (
                  <div className="mt-4 p-3 bg-[#F7FAFC] rounded-lg">
                    <p className="text-sm text-[#4A5568]">
                      Rollos necesarios: <span className="font-semibold text-[#1B2B4D]" data-testid="rolls-total">{results.rolls.total}</span>
                      {" "}({results.rolls.roll_m2} m²/rollo = {results.rolls.total_m2_supplied} m² suministrados)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Adhesive */}
            {results.adhesive && results.adhesive.type !== "none" && results.adhesive.type !== "base_aislante" && (
              <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D]">
                    Adhesivo / Fijación
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-medium text-[#0066FF] mb-2" data-testid="adhesive-name">{results.adhesive.name}</p>
                  {results.adhesive.type === "adhesive" && (
                    <div className="space-y-1 text-sm text-[#4A5568]">
                      <p>Kg totales: <span className="font-semibold text-[#1B2B4D]">{results.adhesive.kg_total}</span></p>
                      <p>{results.adhesive.containers > 1 ? (results.adhesive.container_name || "").replace(/ón$/, "on") + "es" : results.adhesive.container_name} de {results.adhesive.container_size} {results.adhesive.container_unit}: <span className="font-semibold text-[#1B2B4D]" data-testid="adhesive-containers">{results.adhesive.containers}</span></p>
                    </div>
                  )}
                  {results.adhesive.type === "tape" && (
                    <div className="space-y-1 text-sm text-[#4A5568]">
                      <p>Ml totales: <span className="font-semibold text-[#1B2B4D]">{results.adhesive.ml_total}</span></p>
                      <p>Rollos de {results.adhesive.container_size} {results.adhesive.container_unit}: <span className="font-semibold text-[#1B2B4D]">{results.adhesive.rolls}</span></p>
                    </div>
                  )}
                  {results.adhesive.type === "hardware" && (
                    <div className="space-y-1 text-sm text-[#4A5568]">
                      {results.adhesive.items.map((item, i) => (
                        <p key={i}>{item.name}: <span className="font-semibold text-[#1B2B4D]">{item.quantity} {item.unit}</span>
                          {item.containers && <span> ({item.containers} {item.containers > 1 ? (item.container_name || "").replace(/ón$/, "on") + "es" : item.container_name})</span>}
                        </p>
                      ))}
                    </div>
                  )}
                  {results.adhesive.type === "tapes" && (
                    <div className="space-y-1 text-sm text-[#4A5568]">
                      <p>Stickers: <span className="font-semibold text-[#1B2B4D]">{results.adhesive.stickers}</span></p>
                      <p>Rollos ({results.adhesive.stickers_per_roll}/rollo): <span className="font-semibold text-[#1B2B4D]">{results.adhesive.rolls}</span></p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Welding Cord */}
            {results.welding_cord && (
              <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D]">Soldadura Térmica</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-[#4A5568] space-y-1">
                  <p>Tipo: {results.welding_cord.type}</p>
                  <p>Rollos de {results.welding_cord.cord_size} ml: <span className="font-semibold text-[#1B2B4D]">{results.welding_cord.rolls}</span></p>
                  {results.welding_cord.total_ml && <p>Metros lineales: {results.welding_cord.total_ml}</p>}
                </CardContent>
              </Card>
            )}

            {/* Rodapie */}
            {results.rodapie && (
              <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D]">Rodapié</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-[#4A5568] space-y-2">
                  {/* Per-room perimeter breakdown */}
                  {selectedRoomIndices.length > 0 && (() => {
                    const selRooms = selectedRoomIndices.map((i) => aiRooms[i]).filter(Boolean);
                    const hasPerimData = selRooms.some((r) => parseFloat(r.perimeter_without_doors_m) > 0 || parseFloat(r.perimeter_m) > 0);
                    if (!hasPerimData) return null;
                    return (
                      <div className="bg-[#F7FAFC] rounded-lg p-3 mb-2 space-y-1">
                        <p className="text-xs font-semibold text-[#1B2B4D] mb-1">Desglose por estancia:</p>
                        {selRooms.map((r, i) => {
                          const perimFull = parseFloat(r.perimeter_m) || 0;
                          const perimNet = parseFloat(r.perimeter_without_doors_m) || 0;
                          const doors = parseInt(r.doors_count) || 0;
                          return (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-[#4A5568]">{r.name || `Estancia ${i + 1}`}</span>
                              <span className="text-[#1B2B4D] font-medium">
                                {perimNet > 0 ? perimNet : perimFull} m
                                {perimNet > 0 && perimFull > 0 && perimNet < perimFull && (
                                  <span className="text-[#718096] font-normal"> ({perimFull} m - {doors} puerta{doors !== 1 ? "s" : ""})</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  <p>Metros lineales: {results.rodapie.length_m} (+7% = {results.rodapie.with_waste_m})</p>
                  <p>Tiras de {results.rodapie.strip_length}m: <span className="font-semibold text-[#1B2B4D]">{results.rodapie.strips}</span></p>
                </CardContent>
              </Card>
            )}

            {/* ESD */}
            {results.esd && (
              <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D]">Pavimento ESD</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-[#4A5568] space-y-1">
                  <p>Cinta de cobre necesaria: {results.esd.ml_needed} ml</p>
                  <p>Rollos de 33 ml: <span className="font-semibold text-[#1B2B4D]">{results.esd.rolls_33ml}</span></p>
                  <p>Metros suministrados: {results.esd.ml_supplied} ml</p>
                </CardContent>
              </Card>
            )}

            {/* Leveling */}
            {results.leveling && (
              <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D]">Nivelación</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-[#4A5568] space-y-1">
                  <p>Autonivelante: {results.leveling.autonivelante_kg} kg (espesor {results.leveling.thickness_mm} mm)</p>
                  <p>Sacos de 25 kg: <span className="font-semibold text-[#1B2B4D]">{results.leveling.bags_25kg}</span></p>
                  <p>Imprimación: {results.leveling.primer_kg} kg</p>
                  <p>Bidones de 5 kg: <span className="font-semibold text-[#1B2B4D]">{results.leveling.drums_5kg}</span></p>
                </CardContent>
              </Card>
            )}

            {/* Base aislante */}
            {results.base_aislante && (
              <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D]">Base Aislante</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-[#4A5568] space-y-1">
                  <p>Rollos necesarios: <span className="font-semibold text-[#1B2B4D]" data-testid="base-aislante-rollos">{results.base_aislante.rollos}</span>
                    {" "}({results.base_aislante.m2_per_roll} m²/rollo = {results.base_aislante.total_m2_supplied} m² suministrados)
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Tools */}
            {form.herramientas && (() => {
              const toolKey = getToolListKey();
              const tools = toolKey ? TOOL_LISTS[toolKey] : null;
              if (!tools) return null;
              return (
                <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
                  <CardHeader className="pb-3">
                    <button
                      className="w-full flex items-center justify-between"
                      onClick={() => setToolsExpanded(!toolsExpanded)}
                      data-testid="tools-toggle"
                    >
                      <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D] flex items-center gap-2">
                        <Wrench className="h-4 w-4 text-[#0066FF]" /> Herramientas
                      </CardTitle>
                      {toolsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  </CardHeader>
                  {toolsExpanded && (
                    <CardContent className="space-y-4">
                      {tools.medición && (
                        <div>
                          <p className="text-xs font-semibold text-[#0066FF] uppercase mb-2">Medición y Replanteo</p>
                          <ul className="space-y-1">
                            {tools.medición.map((t, i) => <li key={i} className="text-sm text-[#4A5568] pl-3 border-l-2 border-[#E2E8F0]">{t}</li>)}
                          </ul>
                        </div>
                      )}
                      {tools.corte && (
                        <div>
                          <p className="text-xs font-semibold text-[#0066FF] uppercase mb-2">Corte, Ajuste y Colocación</p>
                          <ul className="space-y-1">
                            {tools.corte.map((t, i) => <li key={i} className="text-sm text-[#4A5568] pl-3 border-l-2 border-[#E2E8F0]">{t}</li>)}
                          </ul>
                        </div>
                      )}
                      {tools.acabado && (
                        <div>
                          <p className="text-xs font-semibold text-[#0066FF] uppercase mb-2">Acabado y Remates</p>
                          <ul className="space-y-1">
                            {tools.acabado.map((t, i) => <li key={i} className="text-sm text-[#4A5568] pl-3 border-l-2 border-[#E2E8F0]">{t}</li>)}
                          </ul>
                        </div>
                      )}
                      {/* Welding tools */}
                      {form.soldadura_termica !== "No" && (
                        <div>
                          <p className="text-xs font-semibold text-[#FFA500] uppercase mb-2">Soldadura Térmica</p>
                          <ul className="space-y-1">
                            {WELDING_TOOLS.map((t, i) => <li key={i} className="text-sm text-[#4A5568] pl-3 border-l-2 border-[#FFA500]/30">{t}</li>)}
                          </ul>
                        </div>
                      )}
                      {/* Leveling tools */}
                      {form.nivelacion && (
                        <div>
                          <p className="text-xs font-semibold text-[#0066FF] uppercase mb-2">Nivelación</p>
                          {Object.entries(LEVELING_TOOLS).map(([cat, items]) => (
                            <div key={cat} className="mb-2">
                              <p className="text-xs font-medium text-[#4A5568] capitalize mb-1">{cat}</p>
                              <ul className="space-y-1">
                                {items.map((t, i) => <li key={i} className="text-sm text-[#4A5568] pl-3 border-l-2 border-[#E2E8F0]">{t}</li>)}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })()}

            {/* Version History */}
            {isEditing && versions.length > 0 && (
              <Card className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
                <CardHeader className="pb-3">
                  <button
                    className="w-full flex items-center justify-between"
                    onClick={() => setShowVersions(!showVersions)}
                    data-testid="versions-toggle"
                  >
                    <CardTitle className="font-heading text-base font-semibold text-[#1B2B4D] flex items-center gap-2">
                      <History className="h-4 w-4 text-[#0066FF]" /> Historial de versiones ({versions.length})
                    </CardTitle>
                    {showVersions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </CardHeader>
                {showVersions && (
                  <CardContent>
                    <div className="space-y-2">
                      {versions.map((v) => (
                        <div key={v.version} className="flex items-center justify-between py-2 px-3 bg-[#F7FAFC] rounded-lg text-sm">
                          <div className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 text-[#4A5568]" />
                            <span className="font-medium text-[#1B2B4D]">Version {v.version}</span>
                          </div>
                          <span className="text-xs text-[#4A5568]">
                            {new Date(v.saved_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handlePDF}
                className="bg-[#1B2B4D] hover:bg-[#162340] text-white font-medium flex-1"
                data-testid="download-pdf-btn"
              >
                <Download className="h-4 w-4 mr-2" /> Descargar PDF
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium flex-1"
                data-testid="save-project-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Guardar Proyecto
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a href="https://ventasuelos.com" target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button
                  variant="outline"
                  className="w-full border-[#E2E8F0] bg-white hover:bg-[#F0F4F8] text-[#1B2B4D] font-medium"
                  data-testid="solicitar-presupuesto-btn"
                >
                  <span className="mr-2">&#127968;</span> Solicitar presupuesto de suelo
                </Button>
              </a>
              <a href="https://ventaherramientas.com" target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button
                  variant="outline"
                  className="w-full border-[#E2E8F0] bg-white hover:bg-[#F0F4F8] text-[#1B2B4D] font-medium"
                  data-testid="comprar-herramientas-btn"
                >
                  <span className="mr-2">&#128295;</span> Comprar herramientas de instalación
                </Button>
              </a>
            </div>

            <div className="flex justify-start">
              <Button variant="outline" onClick={() => setStep(3)} className="border-[#E2E8F0]" data-testid="prev-step-4">
                <ArrowLeft className="h-4 w-4 mr-1" /> Modificar parámetros
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Grupo Ventasuelos */}
      <GrupoVentasuelos />

      {/* Footer */}
      <footer className="bg-[#F0F4F8] py-4 mt-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs text-[#4A5568]">Planimetrik &copy; {new Date().getFullYear()} - Todos los derechos reservados.</p>
          <p className="text-xs text-[#718096] mt-0.5">A Ventasuelos SL Company</p>
        </div>
      </footer>
    </div>
  );
}
